import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  organizationField,
  optionalMessageField,
} from "@core/schemas/fields";
import { withEmailConfirmation } from "@core/schemas/refinements";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Dialog } from "@themes/pollinator/ui/Dialog";

export const addYourInstitutionSchema = withEmailConfirmation(
  z.object({
    firstName: firstNameField,
    lastName: lastNameField,
    email: emailField,
    confirmEmail: confirmEmailField,
    organization: organizationField,
    message: optionalMessageField,
  })
);

export type AddYourInstitutionFormData = z.infer<
  typeof addYourInstitutionSchema
>;

export type Props = {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when the form is submitted with valid data */
  onSubmit: (data: AddYourInstitutionFormData) => Promise<void>;
};

/**
 * A dialog form for institutions to request becoming a Sending Institution.
 * This is a pure presentational component - all business logic (API calls,
 * event tracking, dialog state) should be handled by the parent.
 *
 * @example
 * <MarketingAddYourInstitutionDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={async (data) => {
 *     await apiRequest("/api/institution", "POST", data);
 *   }}
 * />
 */
export function MarketingAddYourInstitutionDialog({
  isOpen,
  onClose,
  onSubmit,
}: Props) {
  const { form, handleSubmit, handleClose } = useForm({
    schema: addYourInstitutionSchema,
    onSubmit,
    onClose,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      organization: "",
      message: "",
    },
  });

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={isSubmitSuccessful ? "Success!" : "Add your Alumni"}
      description={
        isSubmitSuccessful
          ? "Thank you for your interest in becoming a Sending Institution. We'll be in touch soon."
          : "Fill out the form below to get in touch about becoming a Sending Institution for Pollinator's next session."
      }
      isWithActions={!isSubmitSuccessful}
      primaryButtonText="Submit"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleSubmit}
      onSecondaryClick={handleClose}
      primaryButtonProps={{
        disabled: isSubmitting,
        visuallyDisabled: !isSubmitting && !isValid,
        type: "submit",
      }}
      secondaryButtonProps={{
        disabled: isSubmitting,
      }}
    >
      {!isSubmitSuccessful && (
        <Form form={form} className="py-4">
          <FormField
            name="firstName"
            label="First Name"
            component="Input"
            placeholder="Enter your first name"
            required
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="Input"
            placeholder="Enter your last name"
            required
          />
          <FormField
            name="email"
            label="Email"
            component="Input"
            placeholder="Enter your email"
            autoComplete="email"
            required
          />
          <FormField
            name="confirmEmail"
            label="Email Confirmation"
            component="Input"
            placeholder="Confirm your email"
            required
          />
          <FormField
            name="organization"
            label="Organization"
            component="Input"
            placeholder="Enter your organization name"
            required
          />
          <FormField
            name="message"
            label="Message"
            component="Textarea"
            placeholder="Your message"
          />
        </Form>
      )}
    </Dialog>
  );
}
